/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { Button, ButtonOverrides } from "baseui/button";
import { StatefulTooltip } from "baseui/tooltip";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import { getControlBarButtonStyles } from "../../DesignSystem/ButtonColorStyles";
import { getTooltipOverrides } from "../../DesignSystem/TooltipStyles";

export interface ControlBarButtonProps {
  id?: string;
  dataTestid?: string;
  selected?: boolean;
  disabled?: boolean;
  icon: React.ReactNode;
  onClick: () => void;
  style?: React.CSSProperties;
  tooltip?: string;
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
  tooltipOverrides?: any;
}

export const ControlBarButton: React.FC<ControlBarButtonProps> = ({
  dataTestid,
  selected,
  disabled,
  icon,
  onClick,
  style,
  tooltip,
  tooltipPlacement = "right",
  id,
}) => {
  const { currentTheme } = useDexterTheme();

  const overrides: ButtonOverrides = {
    BaseButton: {
      style: getControlBarButtonStyles(currentTheme)({
        $disabled: disabled,
      }),
    },
  };

  const buttonElement = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        ...style,
      }}
    >
      {/* Selection Bar Div */}
      <div
        style={{
          width: "4px",
          height: "34px",
          background: selected
            ? currentTheme.colors.primaryBlue
            : "transparent",
          borderRadius: "2px",
          transition: "background 0.18s",
          marginRight: "6px",
        }}
      />
      <Button
        data-testid={dataTestid}
        id={id}
        overrides={overrides}
        kind="secondary"
        onClick={onClick}
        disabled={disabled}
        type="button"
        style={{
          padding: 0,
          minWidth: 0,
          height: "34px",
          width: "34px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Button>
    </div>
  );

  return (
    <div data-testid={id}>
      {tooltip ? (
        <StatefulTooltip
          content={tooltip}
          placement={tooltipPlacement}
          overrides={getTooltipOverrides(currentTheme)}
          showArrow
        >
          {buttonElement}
        </StatefulTooltip>
      ) : (
        buttonElement
      )}
    </div>
  );
};
